import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Icons } from './Icons';
import NilVisionLogo from './NilVisionLogo';
import UserAvatar from './UserAvatar';
import { SIDEBAR_NAV } from '../config/nav';

const BRAND   = '#5A38FD';
const BRAND_L = '#a78bfa';
const SANS    = '"Geist", "Inter", system-ui, sans-serif';
const EASE    = [0.22, 1, 0.36, 1];

const NAV_ITEM = (active) => ({
  display: 'flex', alignItems: 'center', gap: 10,
  padding: '11px 14px', borderRadius: 10, width: '100%',
  fontSize: '0.9rem', fontWeight: active ? 600 : 500,
  color: active ? '#fff' : 'rgba(255,255,255,0.45)',
  cursor: 'pointer', border: 'none', textAlign: 'left',
  background: active ? 'rgba(90,56,253,0.14)' : 'transparent',
  boxShadow: active ? 'inset 0 0 0 1px rgba(90,56,253,0.2)' : 'none',
  fontFamily: SANS, transition: 'color 160ms, background 160ms',
});

export default function MobileNavDrawer({ isOpen, onClose, activeComponent, onNavigate, profile, signOut, ghostDraftPending }) {
  const handleNavClick = (id) => { onNavigate(id); onClose(); };

  return (
    <>
      <style>{`
        .nv-drawer { display: block; }
        @media (min-width: 768px) { .nv-drawer { display: none; } }
        .nv-drawer-item:hover { background: rgba(255,255,255,0.04) !important; color: rgba(255,255,255,0.75) !important; }
        .nv-drawer-item.active:hover { background: rgba(90,56,253,0.18) !important; }
      `}</style>

      <div className="nv-drawer">
        {/* Backdrop */}
        <AnimatePresence>
          {isOpen && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.22 }}
              onClick={onClose}
              style={{ position: 'fixed', inset: 0, zIndex: 998, background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}
            />
          )}
        </AnimatePresence>

        {/* Drawer panel */}
        <motion.div
          initial={false}
          animate={{ x: isOpen ? 0 : '-100%' }}
          transition={{ duration: 0.28, ease: EASE }}
          style={{
            position: 'fixed', top: 0, left: 0, bottom: 0, width: 272, zIndex: 999,
            background: 'rgba(10,10,18,0.98)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
            borderRight: '1px solid rgba(255,255,255,0.08)',
            display: 'flex', flexDirection: 'column', overflowY: 'auto',
            boxShadow: '4px 0 40px rgba(0,0,0,0.6)',
          }}
        >
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)',
            paddingBottom: 16, paddingLeft: 20, paddingRight: 16,
            borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <NilVisionLogo size={26} showWordmark={false} />
              <span style={{
                fontSize: '0.85rem', fontWeight: 800, letterSpacing: '0.08em',
                background: 'linear-gradient(135deg, #fff 0%, #c4b5fd 55%, #93c5fd 100%)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
              }}>NILVISION</span>
            </div>
            <motion.button whileTap={{ scale: 0.9 }} onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 8, cursor: 'pointer', padding: 7, color: 'rgba(255,255,255,0.4)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
              <Icons.Close style={{ width: 16, height: 16 }} />
            </motion.button>
          </div>

          {/* Nav items */}
          <nav style={{ flex: 1, padding: '10px 10px', overflowY: 'auto' }}>
            {SIDEBAR_NAV.map((item) => {
              const IconComponent = Icons[item.icon] || Icons.Home;
              const active  = activeComponent === item.id;
              const showDot = item.id === 'ghost' && ghostDraftPending && !active;
              return (
                <motion.button key={item.id} whileTap={{ scale: 0.97 }}
                  onClick={() => handleNavClick(item.id)}
                  className={`nv-drawer-item${active ? ' active' : ''}`}
                  style={{ ...NAV_ITEM(active), marginBottom: 2 }}>
                  <div style={{ position: 'relative', flexShrink: 0, display: 'flex' }}>
                    <IconComponent style={{ width: 19, height: 19 }} />
                    {showDot && (
                      <motion.div animate={{ scale: [1, 1.25, 1], opacity: [0.8, 1, 0.8] }} transition={{ repeat: Infinity, duration: 2 }}
                        style={{ position: 'absolute', top: -3, right: -4, width: 7, height: 7, borderRadius: '50%', background: BRAND_L, boxShadow: `0 0 0 2px #0A0A12, 0 0 6px rgba(167,139,250,0.7)` }} />
                    )}
                  </div>
                  <span>{item.label}</span>
                </motion.button>
              );
            })}
          </nav>

          {/* Footer */}
          <div style={{ padding: '10px', borderTop: '1px solid rgba(255,255,255,0.06)', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <motion.button whileTap={{ scale: 0.97 }}
              onClick={() => handleNavClick('profile')}
              className={`nv-drawer-item${activeComponent === 'profile' ? ' active' : ''}`}
              style={{ ...NAV_ITEM(activeComponent === 'profile'), marginBottom: 2 }}>
              <UserAvatar profile={profile} size={22} />
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {profile?.first_name || 'Profile'}
              </span>
            </motion.button>

            {profile?.is_admin && (
              <motion.button whileTap={{ scale: 0.97 }}
                onClick={() => { globalThis.location.href = '/admin'; }}
                className="nv-drawer-item"
                style={{ ...NAV_ITEM(false), color: BRAND_L, fontSize: '0.85rem', marginBottom: 2 }}>
                <Icons.Crown style={{ width: 17, height: 17 }} />
                <span>Admin</span>
              </motion.button>
            )}

            <motion.button whileTap={{ scale: 0.97 }}
              onClick={signOut}
              className="nv-drawer-item"
              style={{ ...NAV_ITEM(false), color: 'rgba(255,255,255,0.22)', fontSize: '0.85rem' }}>
              <Icons.Logout style={{ width: 17, height: 17, opacity: 0.5 }} />
              <span>Sign Out</span>
            </motion.button>
          </div>
        </motion.div>
      </div>
    </>
  );
}
